import { formatRatingIn, toLocale } from '@zuruflex/i18n';
import type { Vehicle } from '@zuruflex/types';
import { Money, RatingStars, Skeleton } from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

/**
 * The shot a customer sees first.
 *
 * The front three-quarter is the cover by convention — it is the one the
 * add-car form marks as such — so it is the one shown here rather than
 * whichever photograph happens to sort first.
 */
function cover(car: Vehicle) {
  return car.images.find((image) => image.kind === 'FRONT_THREE_QUARTER') ?? car.images[0];
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-xs text-text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * One car at a glance: what it looks like, what it is, and what it has done.
 *
 * It sits beside the tabs rather than inside one because it answers the
 * questions that do not belong to any single step — is this the right car,
 * and has it been worth keeping on the books.
 */
export function CarSummary({ car }: { car: Vehicle }) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);

  /*
   * The same query keys the bookings and reviews tabs use, so opening
   * either costs nothing: react-query serves both from one fetch.
   */
  const bookings = useQuery({
    queryKey: ['vehicle-bookings', car.id],
    queryFn: () => api.listBookings({ vehicleId: car.id, perPage: 100 }),
  });
  const reviews = useQuery({
    queryKey: ['vehicle-reviews', car.id],
    queryFn: () => api.listReviews({ vehicleId: car.id, side: 'GUEST_ON_LISTING', perPage: 100 }),
  });

  // A vehicle carries only its branch's id, and the details form has the
  // list already — same key, so this costs nothing.
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.listLocations(),
  });
  const branch = locations?.find((location) => location.id === car.locationId);

  const completed = (bookings.data?.items ?? []).filter((hire) => hire.status === 'COMPLETED');
  const earned = completed.reduce((total, hire) => total + (hire.partnerEarnings ?? 0), 0);

  const scored = (reviews.data?.items ?? []).filter((review) => review.status === 'PUBLISHED');
  const rating =
    scored.length > 0
      ? Math.round(scored.reduce((total, review) => total + review.overall, 0) / scored.length)
      : null;

  const photo = cover(car);

  return (
    <div className="overflow-hidden rounded-card border border-border-default bg-surface-raised">
      {photo ? (
        <img src={photo.url} alt={photo.alt ?? ''} className="aspect-[4/3] w-full object-cover" />
      ) : (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 bg-surface-sunken text-text-muted">
          <ImageOff className="size-6" aria-hidden />
          <span className="text-xs">{t('fleet.needsPhotos')}</span>
        </div>
      )}

      <div className="p-4">
        <Money
          amount={car.dailyRate}
          currency={car.currency}
          amountClassName="font-display text-xl font-bold"
          suffix={<span className="text-xs text-text-muted">{t('common:unit.perDay')}</span>}
        />

        <dl className="mt-3 divide-y divide-border-default border-t border-border-default">
          <Row label={t('common:field.plate')}>
            <span className="font-mono">{car.plateNumber}</span>
          </Row>
          <Row label={t('common:field.class')}>{t(`common:vehicleClass.${car.vehicleClass}`)}</Row>
          <Row label={t('common:field.transmission')}>
            {t(`common:transmission.${car.transmission}`)}
          </Row>
          <Row label={t('common:field.fuel')}>{t(`common:fuelType.${car.fuelType}`)}</Row>
          <Row label={t('common:field.seats')}>{car.seats}</Row>
          <Row label={t('common:field.doors')}>{car.doors}</Row>
          {branch ? <Row label={t('addCar.pickUpPoint')}>{branch.name}</Row> : null}
          <Row label={t('common:field.driver')}>{t(`common:driverPolicy.${car.driverPolicy}`)}</Row>
          {car.driverDailyRate ? (
            <Row label={t('common:field.driverRate')}>
              <Money amount={car.driverDailyRate} currency={car.currency} />
            </Row>
          ) : null}
        </dl>

        {/*
          What it has actually done. Kept out of the specification list
          above because a partner reads the two for different reasons —
          one to check the listing, one to decide whether to keep the car.
        */}
        <p className="mt-5 text-[11px] font-bold uppercase tracking-widest text-text-muted">
          {t('carDetail.summary')}
        </p>

        <dl className="mt-1 divide-y divide-border-default border-t border-border-default">
          <Row label={t('carDetail.timesLet')}>
            {bookings.isPending ? <Skeleton className="h-4 w-8" /> : completed.length}
          </Row>
          <Row label={t('carDetail.earnedFromCar')}>
            {bookings.isPending ? (
              <Skeleton className="h-4 w-20" />
            ) : (
              <Money amount={earned} currency={car.currency} />
            )}
          </Row>
          <Row label={t('carDetail.tab.reviews')}>
            {reviews.isPending ? (
              <Skeleton className="h-4 w-16" />
            ) : rating === null ? (
              <span className="text-text-muted">—</span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <RatingStars value={rating / 100} size="sm" />
                {formatRatingIn(rating, locale)}
              </span>
            )}
          </Row>
        </dl>
      </div>
    </div>
  );
}
